<template>
  <TheCard class="tw-overflow-visible">
    <CardHeader :showTitle="true" :showActions="true" title="Profile">
      <template #card-header-actions>
        <template v-if="!isEditingDetails">
          <TheButton
            variant="primary"
            size="small"
            @click="editMemberDetails()"
            :hasLeadingIcon="true"
          >
            <template #leading-icon><b-icon-pencil /></template>
            <template #text> Edit </template>
          </TheButton>
        </template>
        <template v-if="isEditingDetails">
          <div class="tw-flex tw-gap-2">
            <TheButton
              :disabled="!formComplete || isSaving"
              variant="success"
              size="small"
              @click="upsertUser()"
            >
              <template #text> Save </template>
            </TheButton>
            <TheButton
              variant="tertiary"
              size="small"
              @click="cancelEdit()"
              :hasLeadingIcon="true"
            >
              <template #text> Cancel </template>
            </TheButton>
          </div>
        </template>
      </template>
    </CardHeader>
    <TheBox :noTopPadding="true">
      <BlockStack :gap="400">
        <div
          class="sm:tw-flex tw-items-center tw-border-solid tw-border-[0.5px] tw-border-gray-100 tw-border-x-0 tw-border-t-0 tw-pb-4"
        >
          <p
            class="tw-text-gray-900 sm:tw-w-64 sm:tw-flex-none sm:tw-pr-6 tw-m-0"
          >
            First Name<span
              v-if="!memberFirstName && isEditingDetails"
              class="tw-text-red-500 tw-m-0 tw-text-sm tw-ms-2 tw-bg-red-50 tw-py-1 tw-px-2 tw-rounded"
              >required field</span
            >
          </p>
          <input
            v-model="form.firstName"
            type="text"
            name="firstName"
            id="firstName"
            :class="{
              'tw-bg-gray-100 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 tw-text-lg tw-leading-6':
                !isEditingDetails,
              'tw-block tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-600 tw-text-lg tw-leading-6':
                isEditingDetails,
            }"
            class="tw-block tw-max-w-sm tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 tw-text-sm tw-leading-6"
            placeholder="First name"
            :readonly="!isEditingDetails"
          />
        </div>
        <div
          class="sm:tw-flex tw-items-center tw-border-solid tw-border-[0.5px] tw-border-gray-100 tw-border-x-0 tw-border-t-0 tw-pb-4"
        >
          <p
            class="tw-text-gray-900 sm:tw-w-64 sm:tw-flex-none sm:tw-pr-6 tw-m-0"
          >
            Last Name<span
              v-if="!memberLastName && isEditingDetails"
              class="tw-text-red-500 tw-m-0 tw-text-sm tw-ms-2 tw-bg-red-50 tw-py-1 tw-px-2 tw-rounded"
              >required field</span
            >
          </p>
          <input
            v-model="form.lastName"
            type="text"
            name="lastName"
            id="lastName"
            :class="{
              'tw-bg-gray-100 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 tw-text-lg tw-leading-6':
                !isEditingDetails,
              'tw-block tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 focus:tw-ring-2 focus:tw-ring-inset focus:tw-ring-blue-600 tw-text-lg tw-leading-6':
                isEditingDetails,
            }"
            class="tw-block tw-max-w-sm tw-w-full tw-rounded-md tw-border-0 tw-px-2.5 tw-py-1.5 tw-text-gray-900 tw-ring-1 tw-ring-inset tw-ring-gray-300 placeholder:tw-text-gray-400 tw-text-sm tw-leading-6"
            placeholder="Last name"
            :readonly="!isEditingDetails"
          />
        </div>
        <div
          class="sm:tw-flex tw-items-center tw-border-solid tw-border-[0.5px] tw-border-gray-100 tw-border-x-0 tw-border-t-0 tw-pb-4"
        >
          <p
            class="tw-text-gray-900 sm:tw-w-64 sm:tw-flex-none sm:tw-pr-6 tw-m-0"
          >
            Email Address
          </p>
          <div class="tw-flex tw-items-top tw-gap-2">
            <p
              class="tw-text-sm tw-font-bold tw-text-gray-900 tw-text-center tw-m-0"
            >
              {{ memberEmail }}
            </p>
          </div>
        </div>
        <div class="sm:tw-flex tw-items-center">
          <div
            class="tw-flex tw-items-center tw-gap-2 sm:tw-w-64 sm:tw-flex-none sm:tw-pr-6"
          >
            <p class="tw-text-gray-900 tw-m-0">Home Region</p>
            <TheTooltip :toolTip="true" :tooltipContent="regionTooltipContent">
              <template #icon>
                <b-icon-question-circle
                  class="tw-text-black tw-text-xs tw-z-0 tw-cursor-pointer"
                ></b-icon-question-circle>
              </template>
            </TheTooltip>
          </div>
          <div class="tw-flex tw-items-top tw-gap-2">
            <template
              v-if="
                User?.metadata?.homeRegions?.length > 0 && !isEditingDetails
              "
            >
              <p
                class="tw-text-sm tw-font-bold tw-text-gray-900 tw-text-center tw-m-0"
              >
                {{ User?.metadata?.homeRegions[0] || "None" }}
              </p>
            </template>
            <template v-else>
              <ButtonDropdown
                :disabled="!isEditingDetails"
                :id="'home-region-dropdown'"
                :items="toggleRegionOptions()"
                :buttonText="
                  form?.metadata?.homeRegions.length > 0
                    ? form?.metadata?.homeRegions[0]
                    : 'Select home region'
                "
                :alignLeft="false"
                :active="false"
                :hasTrailingIcon="true"
                size="small"
                variant="tertiary"
              />
            </template>
          </div>
        </div>
      </BlockStack>
    </TheBox>
  </TheCard>
</template>

<script>
import TheBox from "../../components-v2/ui/layout/TheBox";
import TheCard from "../../components-v2/ui/layout/TheCard";
import BlockStack from "../../components-v2/ui/layout/BlockStack";
import CardHeader from "../../components-v2/ui/layout/CardHeader";
import TheButton from "../ui/actions/TheButton.vue";
import TheTooltip from "../../components-v2/ui/forms/TheTooltip.vue";
import ButtonDropdown from "../../components-v2/ui/elements/dropdown/ButtonDropdown.vue";
import MultiSelectDropdown from "../../components-v2/ui/elements/dropdown/MultiSelectDropdown.vue";

export default {
  name: "UserProfile",
  components: {
    BlockStack,
    MultiSelectDropdown,
    ButtonDropdown,
    TheTooltip,
    TheButton,
    TheBox,
    TheCard,
    CardHeader,
  },
  data() {
    return {
      member: {},
      form: {
        firstName: "",
        lastName: "",
        email: "",
        metadata: {
          homeRegions: [],
        },
      },
      regions: [
        "Baltimore",
        "Birmingham",
        "Charlotte",
        // "Chicago",
        // "Columbia (MO)",
        // "Ft. Lauderdale",
        "Kansas City",
        "Louisville",
        // "Springfield (MO)",
        "St. Louis",
      ],
      isLoading: true,
      showCampaigns: false,
      showRecipients: false,
      isEditingDetails: false,
      isSaving: false,
    };
  },
  async created() {
    await this.handleCreatedLifecycleLogic();
  },
  computed: {
    User() {
      return this.$store.state.auth.user;
    },
    formComplete() {
      return this.memberFirstName && this.memberLastName;
    },
    memberId() {
      return this?.$store?.state?.auth?.user;
    },
    memberFirstName() {
      return this?.form?.firstName;
    },
    memberLastName() {
      return this?.form?.lastName;
    },
    memberEmail() {
      return this?.form?.email;
    },
    memberHomeRegions() {
      return this?.form?.metadata?.homeRegions;
    },
    memberClientName() {
      return this?.member?.client?.name;
    },
    memberClientDomain() {
      return this?.member?.client?.domains[0];
    },
    // formattedDate() {
    //   const date = moment(this?.member?.createdAt).format("MM/DD/YYYY");
    //   return date;
    // },
    regionTooltipContent() {
      return "Choose your default region to save time when exploring the eGift cards collection.";
    },
    regionOptions() {
      // Sort the regions alphabetically
      const sortedRegions = this.regions.slice().sort();

      // Map over the sorted array to create objects with dynamic IDs
      return sortedRegions.map((region, index) => {
        return {
          id: index + 1,
          label: region,
          action: () => this.setHomeRegions([region]),
        };
      });
    },
    notificationTypes() {
      return this.$store.getters["alerts/getAlertTypes"];
    },
  },
  methods: {
    /** loading member info */
    async loadMemberInformation() {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      const api = await this.getBoldClient();
      this.isLoading = true;

      let requestBody = {
        _id: this.memberId,
      };

      let selection = "client firstName lastName email createdAt recipients";

      const queryParameters = {
        limit: 1,
        sort: "firstName",
        expand: "client recipients",
      };

      try {
        const response = await api.listUsers(queryParameters, {
          filter: requestBody,
          selection: selection,
        });

        this.isLoading = false;
        this.member = response.data.result.records[0];
      } catch (error) {
        console.log("member did not load", error);
        this.isLoading = false;
      }
    },
    async upsertUser() {
      /**@type {import('@boldxchange/sdk/defs').Client}*/

      this.isSaving = true;
      const api = await this.getBoldClient();

      const memberToUpdate = {
        firstName: this.memberFirstName,
        lastName: this.memberLastName,
        ...(this.memberHomeRegions && {
          "metadata.homeRegions": this.memberHomeRegions,
        }),
      };

      try {
        await api.upsertUser(null, {
          filter: { _id: this.memberId },
          patch: memberToUpdate,
        });
        this.handleSuccess(); //
      } catch (error) {
        this.handleErrorFromAPI(error);
        console.log("user was NOT edited", error);
      } finally {
        this.isSaving = false;
      }
    },
    async handleCreatedLifecycleLogic() {
      // await this.loadMemberInformation();
      // await this.checkIfUserHasRecipients();
      await this.$store.dispatch("auth/refreshUser");
      this.initializeForm(this.User);
    },
    setHomeRegions(regions) {
      this.form.metadata.homeRegions = regions;
    },
    toggleRegionOptions() {
      return this.regionOptions;
    },
    editMemberDetails() {
      this.isEditingDetails = !this.isEditingDetails;
    },
    initializeForm(memberData) {
      this.form.firstName = memberData.firstName;
      this.form.lastName = memberData.lastName;
      this.form.email = memberData.email;
      this.form.metadata.homeRegions = memberData?.metadata?.homeRegions || [];
    },
    cancelEdit() {
      this.initializeForm(this.User);
      this.isEditingDetails = false;
    },

    /** handle success and errors */
    handleErrorFromAPI(error) {
      let errorMessage = "Something went wrong. Please try again."; // Default message
      if (error?.response) {
        if (
          error?.response?.data &&
          error?.response?.data?.error &&
          error?.response?.data?.error?.source?.errors
        ) {
          const sourceErrors = error.response.data.error.source.errors;
          const firstErrorKey = Object.keys(sourceErrors)[0];
          errorMessage = sourceErrors[firstErrorKey].message;
        } else {
          errorMessage =
            error?.response?.data?.message || "An unknown error occurred.";
        }
      } else if (error?.request) {
        errorMessage =
          "No response was received from the server. Please check your internet connection.";
      } else {
        errorMessage = "An error occurred with your request. Please try again.";
      }

      // Dispatch the notifyError action to the Vuex store
      this.$store.dispatch("alerts/showAlert", {
        type: this.notificationTypes.ERROR,
        message: errorMessage,
        duration: 6000, // milliseconds before auto-clearing the notification
        // scope: this.$options.name, // Use component's name as the scope
        id: Date.now(), // Ensures each notification is unique
      });

      console.error(errorMessage);
    },
    handleSuccess() {
      this.$store.dispatch("alerts/showAlert", {
        type: this.notificationTypes.SUCCESS,
        message: "Profile successfully updated.",
        duration: 6000,
        id: Date.now(),
      });
      this.isEditingDetails = false;
      this.handleCreatedLifecycleLogic();
    },
  },
};
</script>

<style scoped></style>
